import { useEffect, useState } from 'react';

/**
 * Optional voice-call consent checkbox (Cindy voice, Phase 1b). UNTICKED by
 * default and never required. The text and version come from the server
 * (getVoiceConsentText → ariaConfig.voice), so what the visitor reads is
 * exactly what the CRM records; when the server returns null — the default
 * until Jaco approves the wording — nothing renders and nothing is posted.
 */
interface ConsentText {
  version: string;
  wording: string;
}

let cached: Promise<ConsentText | null> | null = null;
function loadConsentText(): Promise<ConsentText | null> {
  if (!cached) {
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
    cached = fetch(`https://us-central1-${projectId}.cloudfunctions.net/getVoiceConsentText`)
      .then((r) => (r.ok ? r.json() : { consent: null }))
      .then((d: { consent?: ConsentText | null }) => d.consent ?? null)
      .catch(() => null);
  }
  return cached;
}

export function useVoiceConsent() {
  const [consent, setConsent] = useState<ConsentText | null>(null);
  const [ticked, setTicked] = useState(false);
  useEffect(() => {
    let live = true;
    loadConsentText().then((c) => live && setConsent(c));
    return () => {
      live = false;
    };
  }, []);
  /** Spread into the POST body. Empty unless the visitor ticked the box. */
  const payload = consent && ticked ? { voiceConsent: { ticked: true, version: consent.version } } : {};
  return { consent, ticked, setTicked, payload };
}

export function VoiceConsentCheckbox({
  consent,
  ticked,
  setTicked,
  className = '',
}: {
  consent: ConsentText | null;
  ticked: boolean;
  setTicked: (v: boolean) => void;
  className?: string;
}) {
  if (!consent) return null;
  return (
    <label className={`flex items-start gap-2 text-xs text-gray-600 cursor-pointer ${className}`}>
      <input
        type="checkbox"
        checked={ticked}
        onChange={(e) => setTicked(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
      />
      <span>{consent.wording}</span>
    </label>
  );
}
